import React, { useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useLocation } from "react-router-dom";
import { getAmitasoftSiteUrls } from "../../utils/amitasoftSiteUrls";
import { AMITASOFT_BRAND } from "../../theme/amitasoftBrand";
import { AMITASOFT_TYPO } from "../../theme/amitasoftTypography";

const LOGO = `${process.env.PUBLIC_URL}/images/logo-white.svg`;

/** Matches website-amitasoft Navbar — always underlined links */
const navLinkSx = (active) => ({
  color: AMITASOFT_BRAND.white,
  fontFamily: AMITASOFT_TYPO.fontFamily,
  fontWeight: 400,
  fontSize: { xs: "1rem", xl: "1.125rem" },
  lineHeight: 1.12,
  letterSpacing: "-0.046em",
  textDecoration: "underline",
  textUnderlineOffset: "4px",
  opacity: active ? 1 : 0.92,
  cursor: "pointer",
  whiteSpace: "nowrap",
  background: "none",
  border: "none",
  padding: 0,
  "&:hover": { opacity: 0.8 },
});

const signUpButtonSx = {
  background: AMITASOFT_BRAND.gradientPartner,
  color: AMITASOFT_BRAND.white,
  fontFamily: AMITASOFT_TYPO.fontFamily,
  fontWeight: 700,
  fontSize: { xs: "0.875rem", md: "1rem" },
  textTransform: "uppercase",
  textDecoration: "underline",
  textUnderlineOffset: "4px",
  letterSpacing: "0.045em",
  borderRadius: 0,
  px: 3,
  py: 1.5,
  boxShadow: "none",
  "&:hover": { opacity: 0.9, background: AMITASOFT_BRAND.gradientPartner, boxShadow: "none" },
};

export default function LandingAppBar() {
  const siteUrls = getAmitasoftSiteUrls();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isBlogActive =
    location.pathname === "/blog" || location.pathname.startsWith("/blog/");

  const navItems = [
    { label: "Home", href: siteUrls.marketingHome, external: true },
    { label: "About Us", href: siteUrls.aboutUs, external: true },
    { label: "Products", href: siteUrls.products, external: true },
    { label: "Blogs", href: siteUrls.blogHome, external: false, active: isBlogActive },
    { label: "Contact", href: siteUrls.contactUs, external: true },
  ];

  const openExternal = (href) => {
    window.open(href, "_self");
  };

  const renderNavItem = (item, onNavigate) => {
    if (item.external) {
      return (
        <Typography
          key={item.label}
          component="button"
          onClick={() => {
            openExternal(item.href);
            onNavigate?.();
          }}
          sx={navLinkSx(false)}
        >
          {item.label}
        </Typography>
      );
    }

    return (
      <Typography
        key={item.label}
        component={Link}
        to={item.href}
        onClick={onNavigate}
        sx={navLinkSx(item.active)}
      >
        {item.label}
      </Typography>
    );
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: "linear-gradient(90deg, rgba(106,138,255,0.95) 0%, rgba(137,170,245,0.95) 100%)",
          backdropFilter: "blur(4px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 64, lg: 80 },
              py: { xs: 1, lg: 1.5 },
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box
              component="a"
              href={siteUrls.marketingHome}
              sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}
            >
              <Box
                component="img"
                src={LOGO}
                alt="AmitaSoft"
                sx={{ height: { xs: 32, sm: 36, lg: 40 }, width: "auto" }}
              />
            </Box>

            <Stack
              direction="row"
              spacing={{ xs: 3, xl: 5 }}
              alignItems="center"
              sx={{ display: { xs: "none", lg: "flex" } }}
            >
              {navItems.map((item) => renderNavItem(item))}
              <Button
                onClick={() => openExternal(siteUrls.products)}
                sx={{ ...signUpButtonSx, ml: 0.5 }}
              >
                Sign Up
              </Button>
            </Stack>

            <IconButton
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              sx={{ display: { lg: "none" }, color: AMITASOFT_BRAND.white, ml: "auto", fontSize: "1.5rem" }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      <Toolbar sx={{ minHeight: { xs: 64, lg: 80 } }} />

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: "min(100vw, 320px)",
            background: "linear-gradient(180deg, #6A8AFF 0%, #89AAF5 100%)",
            color: AMITASOFT_BRAND.white,
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1.5 }}>
          <IconButton onClick={() => setMobileOpen(false)} sx={{ color: AMITASOFT_BRAND.white }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List sx={{ px: 2, pt: 2 }}>
          {navItems.map((item) => (
            <ListItemButton
              key={item.label}
              component={item.external ? "button" : Link}
              to={item.external ? undefined : item.href}
              onClick={() => {
                if (item.external) openExternal(item.href);
                setMobileOpen(false);
              }}
              sx={{ borderRadius: 1, mb: 0.5, borderBottom: "1px solid rgba(255,255,255,0.2)" }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontFamily: AMITASOFT_TYPO.fontFamily,
                  fontWeight: 400,
                  fontSize: "1.25rem",
                  textDecoration: "underline",
                  textUnderlineOffset: "4px",
                }}
              />
            </ListItemButton>
          ))}
          <ListItemButton
            onClick={() => {
              openExternal(siteUrls.products);
              setMobileOpen(false);
            }}
            sx={{ mt: 3, ...signUpButtonSx, justifyContent: "center" }}
          >
            <ListItemText
              primary="Sign Up"
              primaryTypographyProps={{
                fontFamily: AMITASOFT_TYPO.fontFamily,
                fontWeight: 700,
                textAlign: "center",
                textDecoration: "underline",
              }}
            />
          </ListItemButton>
        </List>
      </Drawer>
    </>
  );
}
